// ============================================
// Path: /frontend/src/pages/RealEstateAgentsPage.jsx
// Purpose: Directory page displaying all registered real estate agents with subscription tiers
// UPDATED: Mobile-first responsive styling applied
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useState, useEffect, useMemo } from 'react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { AgentCard, AgentCardSkeleton } from '../components/AgentCard';
import { User, Search, ArrowUpDown } from 'lucide-react';
import { usersAPI } from '../api/users.api';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

// ========================================
// MAIN COMPONENT
// ========================================
export default function RealEstateAgentsPage() {
  console.log('🎨 [RealEstateAgentsPage] Component mounted');

  // ========================================
  // COMPONENT STATE
  // ========================================
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('random');

  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    const fetchAgents = async () => {
      console.log('🔵 [RealEstateAgentsPage] Fetching all agents...');
      setLoading(true);
      
      try {
        // Fetch users with any subscription tier (Bronze, Silver, Gold, Platinum)
        // These are users who have paid to list properties
        const data = await usersAPI.getAllAgents({
          subscriptionTier: 'BRONZE,SILVER,GOLD,PLATINUM',
          sortBy: 'fullName',
          sortOrder: 'asc'
        });
        console.log('✅ [RealEstateAgentsPage] Agents received:', data);
        
        // Filter out agents with no listings
        const activeAgents = (data.users || []).filter(agent => 
          agent._count && agent._count.listings > 0
        );
        console.log(`🔍 [RealEstateAgentsPage] Filtered to ${activeAgents.length} agents with listings (from ${data.users?.length || 0} total)`);
        
        setAgents(activeAgents);
        setError(null);
      } catch (err) {
        console.error('❌ [RealEstateAgentsPage] Fetch error:', err);
        setError(err.response?.data?.message || 'Failed to load agents');
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, []);

    // ========================================
    // FILTERED AND SORTED AGENTS
    // ========================================
    const filteredAndSortedAgents = useMemo(() => {
      let filtered = agents;

      // Apply search filter
      if (searchQuery.trim()) {
        filtered = filtered.filter(agent => 
          agent.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          agent.email.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      // Apply sorting
      let sorted = [...filtered];
      switch (sortOption) {
        case 'name-asc':
          sorted.sort((a, b) => a.fullName.localeCompare(b.fullName));
          break;
        case 'name-desc':
          sorted.sort((a, b) => b.fullName.localeCompare(a.fullName));
          break;
        case 'random':
        default:
          // Keep original order (already filtered)
          break;
      }

      return sorted;
    }, [agents, searchQuery, sortOption]);

  // ========================================
  // LOADING STATE
  // ========================================
  if (loading) {
    console.log('⏳ [RealEstateAgentsPage] Loading...');
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
          <div className="text-left mb-4 sm:mb-6 md:mb-8">
            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-1 sm:mb-2">Real Estate Agents</h1>
            <p className="text-xs sm:text-sm md:text-base text-muted-foreground">Browse all of our registered real estate agents.</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 md:gap-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <AgentCardSkeleton key={i} />
            ))}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ========================================
  // RENDER
  // ========================================
  console.log('🎨 [RealEstateAgentsPage] Rendering with agents:', agents.length);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        <div className="text-left mb-4 sm:mb-6 md:mb-8">
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-1 sm:mb-2">Real Estate Agents</h1>
          <p className="text-xs sm:text-sm md:text-base text-muted-foreground">Browse all of our registered real estate agents.</p>
        </div>

        {error && (
          <div className="text-center mb-4 sm:mb-6 md:mb-8">
            <p className="text-destructive text-sm sm:text-base">{error}</p>
          </div>
        )}
        
        {/* Search and Sort Controls */}
        {!loading && agents.length > 0 && (
          <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search agents by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 text-xs sm:text-sm h-9 sm:h-10"
              />
            </div>

            {/* Sort Dropdown */}
            <div className="w-full sm:w-48">
              <Select value={sortOption} onValueChange={setSortOption}>
                <SelectTrigger className="text-xs sm:text-sm h-9 sm:h-10">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="random" className="text-xs sm:text-sm">Random Order</SelectItem>
                  <SelectItem value="name-asc" className="text-xs sm:text-sm">Name (A-Z)</SelectItem>
                  <SelectItem value="name-desc" className="text-xs sm:text-sm">Name (Z-A)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 md:gap-4">
          {filteredAndSortedAgents.map((agent) => (
            <AgentCard 
              key={agent.id}
              agentId={agent.id}
              name={agent.fullName}
              email={agent.email}
            />
          ))}
        </div>

          {/* No Search Results State */}
          {!loading && filteredAndSortedAgents.length === 0 && searchQuery && (
            <div className="text-center py-8 sm:py-12 text-muted-foreground">
              <Search className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 sm:mb-3" />
              <p className="text-sm sm:text-base">No agents found matching "{searchQuery}"</p>
              <button 
                onClick={() => setSearchQuery('')}
                className="text-xs sm:text-sm text-primary hover:underline mt-2"
              >
                Clear search
              </button>
            </div>
          )}

        {!loading && agents.length === 0 && (
          <div className="text-center py-8 sm:py-12 md:py-16 text-muted-foreground">
            <User className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 mx-auto mb-2 sm:mb-3 md:mb-4" />
            <p className="text-sm sm:text-base">No agents have registered yet.</p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}